import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@apollo/client";
import {
  Card, Typography, Input, Button, Modal, Form, Select, Switch, Tag, Tooltip,
  Popconfirm, Empty, Spin, Row, Col, Table, Space, Badge, message,
} from "antd";
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, DeleteOutlined,
  KeyOutlined, StopOutlined, CheckCircleOutlined, CopyOutlined,
  ExclamationCircleOutlined, UserOutlined,
} from "@ant-design/icons";
import {
  GET_ALL_CLIENT_LOGINS,
  CREATE_CLIENT_LOGIN,
  REGENERATE_CLIENT_LOGIN_PASSWORD,
  DEACTIVATE_CLIENT_LOGIN,
  REACTIVATE_CLIENT_LOGIN,
  DELETE_CLIENT_LOGIN,
} from "../../../Queries/clientLogins";
import { GET_ALL_CLIENTS } from "../../../Queries/Users";

const { Title, Text } = Typography;

const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDateOnly = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function ClientLoginsManagement() {
  const userFromUserSlice = useSelector((s) => s.user?.user);
  const userFromAuthSlice = useSelector((s) => s.auth?.userData);
  const currentUser = userFromUserSlice || userFromAuthSlice || {};

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [createOpen, setCreateOpen] = useState(false);
  const [tempPasswordModal, setTempPasswordModal] = useState(null); // { email, password, isRegenerate }
  const [form] = Form.useForm();
  const [useCustomPassword, setUseCustomPassword] = useState(false);

  const { data, loading, refetch } = useQuery(GET_ALL_CLIENT_LOGINS, {
    variables: {
      search: search || undefined,
      isActive: statusFilter === "all" ? undefined : statusFilter === "active",
      page,
      limit,
    },
    fetchPolicy: "network-only",
  });

  const { data: clientsData, loading: clientsLoading } = useQuery(GET_ALL_CLIENTS, {
    fetchPolicy: "cache-first",
  });

  const [createMutation, { loading: creating }] = useMutation(CREATE_CLIENT_LOGIN);
  const [regenerateMutation] = useMutation(REGENERATE_CLIENT_LOGIN_PASSWORD);
  const [deactivateMutation] = useMutation(DEACTIVATE_CLIENT_LOGIN);
  const [reactivateMutation] = useMutation(REACTIVATE_CLIENT_LOGIN);
  const [deleteMutation] = useMutation(DELETE_CLIENT_LOGIN);

  const records = data?.getAllClientLogins?.records || [];
  const totalCount = data?.getAllClientLogins?.totalCount || 0;
  const clients = clientsData?.getAllClients?.response || [];

  const clientOptions = useMemo(
    () => clients
      .filter((c) => c?.clientName && c?.status === "Active")
      .map((c) => ({
        label: c.clientName + (c.agencyName ? ` — ${c.agencyName}` : ""),
        value: c.id,
        clientName: c.clientName,
        emailId: c.emailId,
      })),
    [clients]
  );

  const stats = useMemo(() => ({
    total: records.length,
    active: records.filter((r) => r.isActive).length,
    inactive: records.filter((r) => !r.isActive).length,
  }), [records]);

  const handleOpenCreate = useCallback(() => {
    form.resetFields();
    setUseCustomPassword(false);
    setCreateOpen(true);
  }, [form]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const selectedClient = clientOptions.find((c) => c.value === values.clientId);
      const { data: res } = await createMutation({
        variables: {
          input: {
            clientId: values.clientId,
            clientName: selectedClient?.clientName || "",
            email: values.email.trim().toLowerCase(),
            loginLabel: values.loginLabel || "",
            password: useCustomPassword ? values.password : null,
            createdByEmpid: currentUser?.empid || currentUser?._id || currentUser?.id || "",
            createdByName: [currentUser?.firstname, currentUser?.lastname].filter(Boolean).join(" ") || currentUser?.name || "",
            createdByEmail: currentUser?.email || "",
          },
        },
      });
      const r = res?.createClientLogin;
      if (r?.status === 200) {
        message.success("Client login created");
        setCreateOpen(false);
        setTempPasswordModal({
          email: r.record?.email,
          password: r.tempPassword,
          isRegenerate: false,
        });
        refetch();
      } else {
        message.error(r?.message || "Failed to create login");
      }
    } catch (err) {
      if (err?.errorFields) return; // form validation only
      message.error(err?.message || "Failed to create login");
    }
  };

  const handleRegenerate = async (row) => {
    try {
      const { data: res } = await regenerateMutation({ variables: { id: row.id } });
      const r = res?.regenerateClientLoginPassword;
      if (r?.status === 200) {
        message.success("Password regenerated");
        setTempPasswordModal({ email: row.email, password: r.tempPassword, isRegenerate: true });
        refetch();
      } else {
        message.error(r?.message || "Failed to regenerate");
      }
    } catch (err) {
      message.error(err?.message || "Failed to regenerate");
    }
  };

  const handleDeactivate = async (row) => {
    try {
      const { data: res } = await deactivateMutation({
        variables: {
          id: row.id,
          actorEmpid: currentUser?.empid || currentUser?._id || currentUser?.id || "",
          actorName: [currentUser?.firstname, currentUser?.lastname].filter(Boolean).join(" ") || currentUser?.name || "",
        },
      });
      const r = res?.deactivateClientLogin;
      if (r?.status === 200) { message.success("Login deactivated"); refetch(); }
      else message.error(r?.message || "Failed to deactivate");
    } catch (err) { message.error(err?.message || "Failed to deactivate"); }
  };

  const handleReactivate = async (row) => {
    try {
      const { data: res } = await reactivateMutation({ variables: { id: row.id } });
      const r = res?.reactivateClientLogin;
      if (r?.status === 200) { message.success("Login reactivated"); refetch(); }
      else message.error(r?.message || "Failed to reactivate");
    } catch (err) { message.error(err?.message || "Failed to reactivate"); }
  };

  const handleDelete = async (row) => {
    try {
      const { data: res } = await deleteMutation({ variables: { id: row.id } });
      const r = res?.deleteClientLogin;
      if (r?.status === 200) { message.success("Login deleted"); refetch(); }
      else message.error(r?.message || "Failed to delete");
    } catch (err) { message.error(err?.message || "Failed to delete"); }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => message.success("Copied"),
        () => message.error("Copy failed")
      );
    } else {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); message.success("Copied"); } catch { message.error("Copy failed"); }
      document.body.removeChild(ta);
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          {row.loginLabel ? <Text type="secondary" style={{ fontSize: 11 }}>{row.loginLabel}</Text> : null}
        </Space>
      ),
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      render: (v) => <Text>{v || "—"}</Text>,
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (cb) => <Text style={{ fontSize: 12 }}>{cb?.name || "—"}</Text>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => <Text style={{ fontSize: 12 }}>{fmtDateOnly(v)}</Text>,
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{fmtDate(v)}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>Never</Text>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) => (
        <Space size={4} direction="vertical">
          <Tag color={row.isActive ? "green" : "red"} style={{ margin: 0 }}>
            {row.isActive ? "Active" : "Inactive"}
          </Tag>
          {row.mustChangePassword ? <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>Must change pwd</Tag> : null}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, row) => (
        <Space size="small" wrap>
          <Tooltip title="Regenerate temp password">
            <Popconfirm
              title="Regenerate password?"
              description="The current password will stop working. A new temp password will be shown once."
              okText="Regenerate"
              onConfirm={() => handleRegenerate(row)}
            >
              <Button size="small" icon={<KeyOutlined />}>Reset</Button>
            </Popconfirm>
          </Tooltip>
          {row.isActive ? (
            <Popconfirm title="Deactivate this login?" okText="Deactivate" okType="danger" onConfirm={() => handleDeactivate(row)}>
              <Button size="small" icon={<StopOutlined />} danger>Deactivate</Button>
            </Popconfirm>
          ) : (
            <Button size="small" icon={<CheckCircleOutlined />} type="primary" ghost onClick={() => handleReactivate(row)}>Reactivate</Button>
          )}
          <Popconfirm title="Delete this login permanently?" okText="Delete" okType="danger" onConfirm={() => handleDelete(row)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f6fa", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Client Logins</Title>
          <Text type="secondary">Create and manage login credentials for clients accessing the Blueplanit client portal.</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>Create Login</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small"><Space><Badge color="blue" /><Text strong>{totalCount}</Text><Text type="secondary">Total logins</Text></Space></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small"><Space><Badge status="success" /><Text strong>{stats.active}</Text><Text type="secondary">Active</Text></Space></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small"><Space><Badge status="error" /><Text strong>{stats.inactive}</Text><Text type="secondary">Inactive</Text></Space></Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              placeholder="Search by email, client name, or label"
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              style={{ width: "100%" }}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active only" },
                { value: "inactive", label: "Inactive only" },
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={records}
          columns={columns}
          locale={{ emptyText: <Empty description="No client logins yet" /> }}
          pagination={{
            current: page,
            pageSize: limit,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            onChange: (p, ps) => { setPage(p); setLimit(ps); },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="Create Client Login"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Create Login"
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="clientId"
            label="Client"
            rules={[{ required: true, message: "Please select a client" }]}
          >
            <Select
              placeholder="Search and select a client"
              showSearch
              optionFilterProp="label"
              loading={clientsLoading}
              options={clientOptions}
              notFoundContent={clientsLoading ? <Spin size="small" /> : "No clients found"}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Login Email"
            rules={[
              { required: true, message: "Please enter an email" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input placeholder="contact@client-company.com" prefix={<UserOutlined />} />
          </Form.Item>
          <Form.Item
            name="loginLabel"
            label="Label (optional)"
            tooltip="A friendly label to help your team identify this login (e.g. 'John Smith — HQ Office')"
          >
            <Input placeholder="e.g. John Smith — HQ Office" />
          </Form.Item>
          <Form.Item label="Password">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Space>
                <Switch checked={useCustomPassword} onChange={setUseCustomPassword} />
                <Text>{useCustomPassword ? "Set custom temp password" : "Auto-generate temp password"}</Text>
              </Space>
              {useCustomPassword ? (
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Please enter a password" },
                    { min: 8, message: "At least 8 characters" },
                    { pattern: /[A-Za-z]/, message: "Must contain a letter" },
                    { pattern: /[0-9]/, message: "Must contain a number" },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.Password placeholder="Enter temp password" />
                </Form.Item>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <ExclamationCircleOutlined /> A secure 12-character password will be generated and shown ONCE after creation.
                </Text>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={tempPasswordModal?.isRegenerate ? "New Temp Password" : "Login Created"}
        open={!!tempPasswordModal}
        onCancel={() => setTempPasswordModal(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setTempPasswordModal(null)}>I have saved it</Button>,
        ]}
        closable={false}
        maskClosable={false}
        width={520}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4, padding: 12 }}>
            <Text strong style={{ color: "#d48806" }}>
              <ExclamationCircleOutlined /> This password will not be shown again.
            </Text>
            <br />
            <Text style={{ fontSize: 12, color: "#874d00" }}>
              Share these credentials securely with the client. They will be required to change the password on first login.
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4, marginTop: 4 }}>
              <Text style={{ flex: 1, fontFamily: "monospace" }}>{tempPasswordModal?.email}</Text>
              <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(tempPasswordModal?.email)}>Copy</Button>
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Temporary Password</Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4, marginTop: 4 }}>
              <Text style={{ flex: 1, fontFamily: "monospace", fontSize: 16, letterSpacing: 1 }}>{tempPasswordModal?.password}</Text>
              <Button size="small" type="primary" icon={<CopyOutlined />} onClick={() => copyToClipboard(tempPasswordModal?.password)}>Copy</Button>
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
